import React from 'react';
import { Breadcrumb as AntBreadcrumb, Dropdown } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomeOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

const Breadcrumb: React.FC = () => {
  const { t } = useTranslation(['common']);
  const location = useLocation();

  const getMainMenuItems = (): MenuProps['items'] => [
    {
      key: '/resources/properties',
      label: <Link to="/resources/properties">{t('common:navigation.resourceManagement')}</Link>,
    },
    {
      key: '/community/news',
      label: <Link to="/community/news">{t('common:navigation.communityActions')}</Link>,
    },
    {
      key: '/residents/messages',
      label: <Link to="/residents/messages">{t('common:navigation.residentActions')}</Link>,
    },
    {
      key: '/employee/attendance',
      label: <Link to="/employee/attendance">{t('common:navigation.employeeActions')}</Link>,
    },
    {
      key: '/maintenance',
      label: <Link to="/maintenance">{t('common:navigation.maintenance')}</Link>,
    },
    {
      key: '/finance/payables',
      label: <Link to="/finance/payables">{t('common:navigation.finance')}</Link>,
    },
    {
      key: '/documents',
      label: <Link to="/documents">{t('common:navigation.documents')}</Link>,
    },
    {
      key: '/users',
      label: <Link to="/users">{t('common:navigation.users')}</Link>,
    },
    {
      key: '/settings',
      label: <Link to="/settings">{t('common:navigation.settings')}</Link>,
    },
  ];

  const getResourceMenuItems = (): MenuProps['items'] => [
    {
      key: '/resources/properties',
      label: <Link to="/resources/properties">{t('common:navigation.properties')}</Link>,
    },
    {
      key: '/resources/assets',
      label: <Link to="/resources/assets">{t('common:navigation.assets')}</Link>,
    },
    {
      key: '/resources/employees',
      label: <Link to="/resources/employees">{t('common:navigation.employees')}</Link>,
    },
    {
      key: '/resources/residents',
      label: <Link to="/resources/residents">{t('common:navigation.residents')}</Link>,
    },
    {
      key: '/resources/vendors',
      label: <Link to="/resources/vendors">{t('common:navigation.vendors')}</Link>,
    },
  ];

  const getResidentsMenuItems = (): MenuProps['items'] => [
    {
      key: '/residents/messages',
      label: <Link to="/residents/messages">{t('common:navigation.messages')}</Link>,
    },
    {
      key: '/residents/facility-reservations',
      label: <Link to="/residents/facility-reservations">{t('common:navigation.facilityReservations')}</Link>,
    },
    {
      key: '/residents/visitor-management',
      label: <Link to="/residents/visitor-management">{t('common:navigation.visitorManagement')}</Link>,
    },
    {
      key: '/residents/package-tracking',
      label: <Link to="/residents/package-tracking">{t('common:navigation.packageTracking')}</Link>,
    },
  ];

  const getCommunityMenuItems = (): MenuProps['items'] => [
    {
      key: '/community/news',
      label: <Link to="/community/news">{t('common:navigation.news')}</Link>,
    },
    {
      key: '/community/calendar',
      label: <Link to="/community/calendar">{t('common:navigation.calendar')}</Link>,
    },
    {
      key: '/community/guidelines',
      label: <Link to="/community/guidelines">{t('common:navigation.guidelines')}</Link>,
    },
    {
      key: '/community/expenses',
      label: <Link to="/community/expenses">{t('common:navigation.expenses')}</Link>,
    },
    {
      key: '/community/emergency-alerts',
      label: <Link to="/community/emergency-alerts">{t('common:navigation.emergencyAlerts')}</Link>,
    },
    {
      key: '/community/lost-and-found',
      label: <Link to="/community/lost-and-found">{t('common:navigation.lostAndFound')}</Link>,
    },
    {
      key: '/community/incidents',
      label: <Link to="/community/incidents">{t('common:navigation.incidentReporting')}</Link>,
    },
  ];

  const getEmployeeMenuItems = (): MenuProps['items'] => [
    {
      key: '/employee/attendance',
      label: <Link to="/employee/attendance">{t('common:navigation.attendance')}</Link>,
    },
    {
      key: '/employee/leave-requests',
      label: <Link to="/employee/leave-requests">{t('common:navigation.leaveRequests')}</Link>,
    },
    {
      key: '/employee/tasks',
      label: <Link to="/employee/tasks">{t('common:navigation.tasks')}</Link>,
    },
    {
      key: '/employee/payroll',
      label: <Link to="/employee/payroll">{t('common:navigation.payroll')}</Link>,
    },
  ];

  const getFinanceMenuItems = (): MenuProps['items'] => [
    {
      key: '/finance/payables',
      label: <Link to="/finance/payables">{t('common:navigation.payables')}</Link>,
    },
    {
      key: '/finance/receivables',
      label: <Link to="/finance/receivables">{t('common:navigation.receivables')}</Link>,
    },
    {
      key: '/finance/accounting',
      label: <Link to="/finance/accounting">{t('common:navigation.accounting')}</Link>,
    },
  ];

  const getBreadcrumbItems = () => {
    const pathSnippets = location.pathname.split('/').filter(i => i);
    const extraBreadcrumbItems = pathSnippets.map((snippet, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      let label = '';
      let menuItems: MenuProps['items'] | undefined;

      // First level - Main sections
      if (index === 0) {
        if (url === '/dashboard') {
          label = t('common:navigation.dashboard');
        } else if (url === '/onboarding') {
          label = 'Onboarding';
        } else if (url === '/users' || url.startsWith('/users/')) {
          label = t('common:navigation.users');
          menuItems = getMainMenuItems();
        } else if (url.startsWith('/resources')) {
          label = t('common:navigation.resourceManagement');
          menuItems = getMainMenuItems();
        } else if (url.startsWith('/community')) {
          label = t('common:navigation.communityActions');
          menuItems = getMainMenuItems();
        } else if (url.startsWith('/residents')) {
          label = t('common:navigation.residentActions');
          menuItems = getMainMenuItems();
        } else if (url.startsWith('/employee')) {
          label = t('common:navigation.employeeActions');
          menuItems = getMainMenuItems();
        } else if (url.startsWith('/finance')) {
          label = t('common:navigation.finance');
          menuItems = getMainMenuItems();
        } else if (url === '/maintenance') {
          label = t('common:navigation.maintenance');
          menuItems = getMainMenuItems();
        } else if (url === '/documents') {
          label = t('common:navigation.documents');
          menuItems = getMainMenuItems();
        } else if (url === '/settings' || url === '/workspace') {
          label = t('common:navigation.settings');
          menuItems = getMainMenuItems();
        }
      }
      // Second level - Section pages
      else if (index === 1) {
        if (url.startsWith('/resources/properties')) {
          label = t('common:navigation.properties');
          menuItems = getResourceMenuItems();
        } else if (url.startsWith('/resources/assets')) {
          label = t('common:navigation.assets');
          menuItems = getResourceMenuItems();
        } else if (url.startsWith('/resources/employees')) {
          label = t('common:navigation.employees');
          menuItems = getResourceMenuItems();
        } else if (url.startsWith('/resources/residents')) {
          label = t('common:navigation.residents');
          menuItems = getResourceMenuItems();
        } else if (url.startsWith('/resources/vendors')) {
          label = t('common:navigation.vendors');
          menuItems = getResourceMenuItems();
        } else if (url.startsWith('/residents/messages')) {
          label = t('common:navigation.messages');
          menuItems = getResidentsMenuItems();
        } else if (url.startsWith('/residents/facility-reservations')) {
          label = t('common:navigation.facilityReservations');
          menuItems = getResidentsMenuItems();
        } else if (url.startsWith('/residents/visitor-management')) {
          label = t('common:navigation.visitorManagement');
          menuItems = getResidentsMenuItems();
        } else if (url.startsWith('/residents/package-tracking')) {
          label = t('common:navigation.packageTracking');
          menuItems = getResidentsMenuItems();
        } else if (url.startsWith('/community/news')) {
          label = t('common:navigation.news');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/calendar')) {
          label = t('common:navigation.calendar');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/guidelines')) {
          label = t('common:navigation.guidelines');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/expenses')) {
          label = t('common:navigation.expenses');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/emergency-alerts')) {
          label = t('common:navigation.emergencyAlerts');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/lost-and-found')) {
          label = t('common:navigation.lostAndFound');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/community/incidents')) {
          label = t('common:navigation.incidentReporting');
          menuItems = getCommunityMenuItems();
        } else if (url.startsWith('/employee/attendance')) {
          label = t('common:navigation.attendance');
          menuItems = getEmployeeMenuItems();
        } else if (url.startsWith('/employee/leave-requests')) {
          label = t('common:navigation.leaveRequests');
          menuItems = getEmployeeMenuItems();
        } else if (url.startsWith('/employee/tasks')) {
          label = t('common:navigation.tasks');
          menuItems = getEmployeeMenuItems();
        } else if (url.startsWith('/employee/payroll')) {
          label = t('common:navigation.payroll');
          menuItems = getEmployeeMenuItems();
        } else if (url.startsWith('/finance/payables')) {
          label = t('common:navigation.payables');
          menuItems = getFinanceMenuItems();
        } else if (url.startsWith('/finance/receivables')) {
          label = t('common:navigation.receivables');
          menuItems = getFinanceMenuItems();
        } else if (url.startsWith('/finance/accounting')) {
          label = t('common:navigation.accounting');
          menuItems = getFinanceMenuItems();
        }
      }
      // Third level and beyond - Leaf nodes (no dropdown)
      else {
        if (url.includes('/blocks')) {
          label = t('common:navigation.properties');
        } else if (url.includes('/floors')) {
          label = t('common:navigation.properties');
        } else if (url.includes('/units')) {
          label = t('common:navigation.properties');
        } else if (url.includes('/parking')) {
          label = t('common:navigation.properties');
        } else {
          label = snippet.charAt(0).toUpperCase() + snippet.slice(1).replace(/-/g, ' ');
        }
      }

      const breadcrumbItem = menuItems ? (
        <Dropdown menu={{ items: menuItems }} placement="bottomLeft">
          <span style={{ cursor: 'pointer' }}>
            {label} <DownOutlined />
          </span>
        </Dropdown>
      ) : (
        <Link to={url}>{label}</Link>
      );

      return {
        key: url,
        title: breadcrumbItem,
      };
    });

    const breadcrumbItems = [
      {
        key: 'home',
        title: <Link to="/dashboard"><HomeOutlined /></Link>,
      },
      ...extraBreadcrumbItems,
    ];

    return breadcrumbItems;
  };

  return (
    <AntBreadcrumb
      items={getBreadcrumbItems()}
      style={{ margin: '16px 0' }}
    />
  );
};

export default Breadcrumb; 